import { get, isObject } from 'lodash';
import {
  ONYX_DROPDOWN_GAP,
  ONYX_DROPDOWN_VIEWPORT_MARGIN,
  ONYX_DROPDOWN_VIEWPORT_PADDING,
} from '../components/dropdown/constants';
import type {
  OnyxDropdownConfig,
  OnyxDropdownOptions,
  OnyxDropdownOptionsInput,
  OnyxDropdownOptionsSource,
  OnyxOption,
  OnyxOptionsGroup,
} from '../directives/interfaces';
import { OnyxOverlayPosition } from '../enums';

export class DropdownHelper {
  public static mapToGroups<T>(
    options: OnyxDropdownOptions<T>,
    subheading?: OnyxDropdownConfig<any>['subheading'],
    subheadingTemplateRef?: OnyxDropdownConfig<any>['subheadingTemplateRef'],
  ): OnyxOptionsGroup<T>[] | null {
    if (options == null) return null;
    if (options.length === 0) return [];
    if ('options' in options[0]) {
      return options as OnyxOptionsGroup<T>[];
    }
    return [
      {
        id: '0.default',
        subheading,
        subheadingTemplateRef,
        options: options as OnyxOption<T>[],
      },
    ];
  }

  public static mapToOptions<T>(
    options: OnyxDropdownOptions<T>,
  ): OnyxOption<T>[] | null {
    const groups = DropdownHelper.mapToGroups(options);
    return groups?.flatMap((group) => group.options) ?? null;
  }

  public static updateGroupOptions<T>(
    options: OnyxDropdownOptions<T>,
    map: (options: OnyxOption<T>[]) => OnyxOption<T>[],
  ): OnyxOptionsGroup<T>[] | null {
    const groups = DropdownHelper.mapToGroups(options);
    return (
      groups?.map((group) => ({
        ...group,
        options: map(group.options),
      })) ?? null
    );
  }

  public static isSource<OptionValue, Value>(
    options: OnyxDropdownOptionsInput<OptionValue, Value>,
  ): options is OnyxDropdownOptionsSource<OptionValue, Value> {
    if (!isObject(options)) return false;
    return ['list', 'get', 'idKey'].every((key) => key in options);
  }

  public static getViewportMaxHeight(
    element: HTMLElement,
    positions?: OnyxOverlayPosition[],
  ): string {
    const { top, bottom } = element.getBoundingClientRect();

    const position = positions?.[0] ?? OnyxOverlayPosition.BOTTOM;
    const padding =
      ONYX_DROPDOWN_VIEWPORT_PADDING - ONYX_DROPDOWN_VIEWPORT_MARGIN;
    const offset = padding + ONYX_DROPDOWN_GAP;

    const maxHeight = {
      [OnyxOverlayPosition.TOP]: top - offset,
      [OnyxOverlayPosition.BOTTOM]: window.innerHeight - bottom - offset,
      [OnyxOverlayPosition.LEFT]: window.innerHeight - padding * 2,
      [OnyxOverlayPosition.RIGHT]: window.innerHeight - padding * 2,
    }[position];

    return `${Math.max(maxHeight, 0)}px`;
  }

  public static getCompareValue<OptionValue, Value = OptionValue>(
    value: OptionValue | Value,
    compareKey: OnyxDropdownConfig<OptionValue>['compareKey'],
  ): unknown {
    return compareKey ? (get(value, compareKey) ?? value) : value;
  }

  public static getErrorFields(
    tooltip: string,
  ): Pick<
    OnyxOption<unknown>,
    'rightIcon' | 'rightIconColor' | 'rightIconTooltip' | 'disabled'
  > {
    return {
      rightIcon: { name: 'clear-filled', size: 16 },
      rightIconColor: 'red',
      rightIconTooltip: tooltip,
      disabled: true,
    } as const;
  }
}
